import { LoggerService } from '@ad-lucy/components/LoggerService';
import { useContentConfig, useLocationChange } from '@ad-lucy/context/adData';
import { usePages } from '@ad-lucy/context/pages';
import { useFetch } from '@ad-lucy/hooks/fetch/useFetch';
import { useCallback, useEffect, useRef } from 'react';

import {
  type ConfigurationPaths,
  LiveRenderingEvent,
  type LiveRenderingReceiveMessage,
} from '../types';
import { useReceiveMessage } from './useReceiveMessage';
import { useSendMessage } from './useSendMessage';

const logger = LoggerService.getLogger().getModuleLogger(useMessaging.name);

export function useMessaging(): void {
  const { currentPage, pages, setCurrentPageByIndex } = usePages();
  const { data: { branch } = {} } = useContentConfig();
  const { setFullContentUrl } = useLocationChange();
  const { get } = useFetch<ConfigurationPaths>();
  const sendMessage = useSendMessage();

  const canTriggerChangePage = useRef(false);
  const canTriggerChangeBranch = useRef(false);

  useEffect(() => {
    if (currentPage?.id && canTriggerChangePage.current) {
      sendMessage({
        event: LiveRenderingEvent.CHANGE_PAGE,
        payload: currentPage.id,
      });
    } else if (currentPage?.id) canTriggerChangePage.current = true;
  }, [currentPage?.id, sendMessage]);

  useEffect(() => {
    if (branch?.id && canTriggerChangeBranch.current) {
      sendMessage({
        event: LiveRenderingEvent.CHANGE_BRANCH,
        payload: String(branch.id),
      });
    } else if (branch?.id) canTriggerChangeBranch.current = true;
  }, [branch?.id, sendMessage]);

  const listener = useCallback(
    (data: LiveRenderingReceiveMessage): void => {
      switch (data.event) {
        case LiveRenderingEvent.CHANGE_PAGE: {
          canTriggerChangePage.current = false;

          const pageIndex = pages.findIndex((page) => page.id === data.payload);
          if (pageIndex !== -1) setCurrentPageByIndex(pageIndex);
          break;
        }
        case LiveRenderingEvent.CHANGE_BRANCH: {
          canTriggerChangeBranch.current = false;

          if (data.payload) {
            // A link to the new configuration is expected
            get(data.payload)
              .then((response) => response.contentPath)
              .then((contentPath) => contentPath && setFullContentUrl(contentPath))
              .catch((reason) => logger.error(`Failed to load '${data.payload}'`, reason));
          }
          break;
        }
        default:
          return;
      }
    },
    [pages, setCurrentPageByIndex, get, setFullContentUrl],
  );

  useReceiveMessage({ callback: listener });
}
